import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const ResidentDashboard = () => {
  const [user, setUser] = useState(null);
  const [resident, setResident] = useState(null);
  const [lease, setLease] = useState(null);
  const [rentPayments, setRentPayments] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [newMaintenanceRequest, setNewMaintenanceRequest] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium'
  });

  const checkUser = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUser(user);
        await fetchResidentData(user.id);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkUser();
  }, [checkUser]);

  const fetchResidentData = async (userId) => {
    try {
      // Fetch resident profile - if not found, get demo resident
      let { data: residentData } = await supabase
        .from('residents')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      // If no resident found for this user, use demo resident
      if (!residentData) {
        const { data: demoResident } = await supabase
          .from('residents')
          .select('*')
          .eq('account_type', 'resident')
          .limit(1)
          .single();
        residentData = demoResident;
      }
      
      setResident(residentData);

      if (residentData) {
        // Fetch active lease
        const { data: leaseData } = await supabase
          .from('leases')
          .select(`
            *,
            units(*, properties(name, address))
          `)
          .eq('resident_id', residentData.id)
          .eq('status', 'active')
          .single();
        setLease(leaseData);

        // Fetch rent payments
        if (leaseData) {
          const { data: paymentsData } = await supabase
            .from('rent_payments')
            .select('*')
            .eq('lease_id', leaseData.id)
            .order('due_date', { ascending: false });
          setRentPayments(paymentsData || []);
        }

        // Fetch maintenance requests
        const { data: maintenanceData } = await supabase
          .from('maintenance_requests')
          .select('*')
          .eq('resident_id', residentData.id)
          .order('created_at', { ascending: false });
        setMaintenanceRequests(maintenanceData || []);

        // Fetch messages
        const { data: messagesData } = await supabase
          .from('messages')
          .select('*')
          .eq('recipient_id', userId)
          .order('created_at', { ascending: false });
        setMessages(messagesData || []);
      }
    } catch (error) {
      console.error('Error fetching resident data:', error);
    }
  };

  const submitMaintenanceRequest = async (e) => {
    e.preventDefault();
    try {
      await supabase.from('maintenance_requests').insert({
        resident_id: resident.id,
        unit_id: lease.unit_id,
        title: newMaintenanceRequest.title,
        description: newMaintenanceRequest.description,
        category: newMaintenanceRequest.category,
        priority: newMaintenanceRequest.priority
      });
      
      setNewMaintenanceRequest({ title: '', description: '', category: '', priority: 'medium' });
      setShowMaintenanceModal(false);
      fetchResidentData(user.id);
    } catch (error) {
      console.error('Error submitting maintenance request:', error);
    }
  };

  const processRentPayment = async () => {
    try {
      const nextPayment = rentPayments.find(p => p.status === 'pending');
      if (nextPayment) {
        await supabase
          .from('rent_payments')
          .update({ 
            status: 'paid', 
            paid_date: new Date().toISOString().split('T')[0],
            payment_method: 'online'
          })
          .eq('id', nextPayment.id);
        
        setShowPaymentModal(false);
        fetchResidentData(user.id);
      }
    } catch (error) {
      console.error('Error processing payment:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>Loading...</h3>
        </div>
      </Layout>
    );
  }
  
  if (!user) {
    window.location.href = '/';
    return null;
  }

  const nextPayment = rentPayments.find(p => p.status === 'pending');
  const openRequests = maintenanceRequests.filter(r => r.status === 'open' || r.status === 'in_progress');

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '30px' }}>
          <h2>Resident Portal</h2>
          {resident && lease ? (
            <p style={{ color: '#666' }}>
              {lease.units?.properties?.name} - Unit {lease.units?.unit_number}
            </p>
          ) : (
            <p style={{ color: '#666' }}>Loading property information...</p>
          )}
        </div>

        {/* Quick Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
            <h4>Next Rent Due</h4>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: nextPayment ? '#dc3545' : '#28a745' }}>
              {nextPayment ? `$${nextPayment.amount}` : 'Paid'}
            </p>
            <p style={{ fontSize: '12px', color: '#666' }}>
              {nextPayment ? new Date(nextPayment.due_date).toLocaleDateString() : 'Up to date'}
            </p>
          </div>
          
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
            <h4>Open Requests</h4>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
              {openRequests.length}
            </p>
            <p style={{ fontSize: '12px', color: '#666' }}>Maintenance requests</p>
          </div>
          
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
            <h4>Lease Expires</h4>
            <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#17a2b8' }}>
              {lease ? new Date(lease.lease_end).toLocaleDateString() : 'N/A'}
            </p>
            <p style={{ fontSize: '12px', color: '#666' }}>End date</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '30px', flexWrap: 'wrap' }}>
          <button 
            onClick={() => setActiveTab('overview')}
            className={`btn ${activeTab === 'overview' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Overview
          </button>
          <button 
            onClick={() => setActiveTab('payments')}
            className={`btn ${activeTab === 'payments' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Rent Payments
          </button>
          <button 
            onClick={() => setActiveTab('maintenance')}
            className={`btn ${activeTab === 'maintenance' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Maintenance
          </button>
          <button 
            onClick={() => setActiveTab('messages')}
            className={`btn ${activeTab === 'messages' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Messages
          </button>
          <button 
            onClick={() => setActiveTab('documents')}
            className={`btn ${activeTab === 'documents' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Documents
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
              <h4>Pay Rent</h4>
              <p>Make your monthly rent payment online</p>
              <button 
                onClick={() => setShowPaymentModal(true)}
                className="btn btn-primary"
                disabled={!nextPayment}
              >
                {nextPayment ? `Pay $${nextPayment.amount}` : 'No Payment Due'}
              </button>
            </div>
            
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
              <h4>Maintenance Request</h4>
              <p>Submit a new maintenance request</p>
              <button 
                onClick={() => setShowMaintenanceModal(true)}
                className="btn btn-primary"
              >
                New Request
              </button>
            </div>
            
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
              <h4>Documents</h4>
              <p>View lease and important documents</p>
              <button className="btn btn-primary">View Documents</button>
            </div>
            
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
              <h4>Contact Management</h4>
              <p>Send a message to property management</p>
              <button className="btn btn-primary">Send Message</button>
            </div>
          </div>
        )}

        {/* Rent Payments Tab */}
        {activeTab === 'payments' && (
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <h3>Rent Payment History</h3>
            <div style={{ display: 'grid', gap: '10px', marginTop: '20px' }}>
              {rentPayments.map(payment => (
                <div key={payment.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
                  <div>
                    <strong>${payment.amount}</strong>
                    <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                      Due: {new Date(payment.due_date).toLocaleDateString()}
                    </p>
                    {payment.paid_date && (
                      <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                        Paid: {new Date(payment.paid_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    background: payment.status === 'paid' ? '#d4edda' : payment.status === 'late' ? '#f8d7da' : '#fff3cd',
                    color: payment.status === 'paid' ? '#155724' : payment.status === 'late' ? '#721c24' : '#856404'
                  }}>
                    {payment.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Maintenance Tab */}
        {activeTab === 'maintenance' && (
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Maintenance Requests</h3>
              <button onClick={() => setShowMaintenanceModal(true)} className="btn btn-primary">
                New Request
              </button>
            </div>
            <div style={{ display: 'grid', gap: '15px' }}>
              {maintenanceRequests.map(request => (
                <div key={request.id} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <h4>{request.title}</h4>
                      <p style={{ color: '#666', margin: '5px 0' }}>{request.description}</p>
                      <p style={{ fontSize: '12px', color: '#999' }}>
                        {new Date(request.created_at).toLocaleDateString()} - {request.category}
                      </p>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: request.status === 'completed' ? '#d4edda' : request.status === 'in_progress' ? '#fff3cd' : '#f8d7da',
                      color: request.status === 'completed' ? '#155724' : request.status === 'in_progress' ? '#856404' : '#721c24'
                    }}>
                      {request.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <h3>Messages</h3>
            <div style={{ display: 'grid', gap: '10px', marginTop: '20px' }}>
              {messages.map(message => (
                <div key={message.id} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
                  <strong>{message.subject}</strong>
                  <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                    {new Date(message.created_at).toLocaleDateString()}
                  </p>
                  <p>{message.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
            <h3>Documents</h3>
            <div style={{ display: 'grid', gap: '10px', marginTop: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
                <div>
                  <strong>Lease Agreement</strong>
                  <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>Signed lease document</p>
                </div>
                <button className="btn btn-secondary">Download</button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', background: '#f8f9fa', borderRadius: '6px' }}>
                <div>
                  <strong>Move-in Checklist</strong>
                  <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>Property condition report</p>
                </div>
                <button className="btn btn-secondary">Download</button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', maxWidth: '400px', width: '90%' }}>
              <h3>Pay Rent</h3>
              {nextPayment && (
                <div>
                  <p>Amount: <strong>${nextPayment.amount}</strong></p>
                  <p>Due Date: {new Date(nextPayment.due_date).toLocaleDateString()}</p>
                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button onClick={processRentPayment} className="btn btn-primary">Pay Now</button>
                    <button onClick={() => setShowPaymentModal(false)} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Maintenance Request Modal */}
        {showMaintenanceModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%' }}>
              <h3>Submit Maintenance Request</h3>
              <form onSubmit={submitMaintenanceRequest}>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={newMaintenanceRequest.title}
                    onChange={(e) => setNewMaintenanceRequest({...newMaintenanceRequest, title: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={newMaintenanceRequest.category}
                    onChange={(e) => setNewMaintenanceRequest({...newMaintenanceRequest, category: e.target.value})}
                    required
                  >
                    <option value="">Select category</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="electrical">Electrical</option>
                    <option value="hvac">HVAC</option>
                    <option value="appliance">Appliance</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={newMaintenanceRequest.priority}
                    onChange={(e) => setNewMaintenanceRequest({...newMaintenanceRequest, priority: e.target.value})}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={newMaintenanceRequest.description}
                    onChange={(e) => setNewMaintenanceRequest({...newMaintenanceRequest, description: e.target.value})}
                    rows="4"
                    style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}
                    required
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button type="submit" className="btn btn-primary">Submit Request</button>
                  <button type="button" onClick={() => setShowMaintenanceModal(false)} className="btn btn-secondary">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ResidentDashboard;